import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PlayCircle, Terminal, BookOpen } from "lucide-react";

export default function Playbooks() {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [target, setTarget] = useState({});

  const load = async () => {
    const [p, r] = await Promise.all([api.get("/playbooks"), api.get("/playbooks/runs")]);
    setItems(p.data);
    setRuns(r.data);
  };

  useEffect(() => { load(); }, []);

  const run = async (pb) => {
    try {
      await api.post(`/playbooks/${pb.id}/run`, { target: target[pb.id] || pb.target });
      toast.success(`Playbook '${pb.name}' executed`);
      load();
    } catch (err) {
      toast.error("Playbook failed");
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Automated response templates</div>
        <h1 className="font-display font-black text-5xl mt-2">Playbooks</h1>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="playbook-grid">
        {items.map((pb) => (
          <div key={pb.id} className="border border-white/10 bg-[#121212] rounded-sm p-5 flex flex-col" data-testid={`playbook-${pb.id}`}>
            <div className="flex items-start gap-3 mb-2">
              <BookOpen className="w-5 h-5 text-[#00F0FF] shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <div className="font-display font-black text-lg leading-tight">{pb.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mt-0.5">{pb.category} · target: {pb.target}</div>
              </div>
            </div>
            <p className="text-sm text-[#8E9196] mb-3">{pb.description}</p>

            <div className="border-t border-white/5 pt-3 mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-2">Steps</div>
              <ol className="space-y-1">
                {pb.steps.map((s) => (
                  <li key={s.order} className="flex gap-2 font-mono text-xs items-center">
                    <span className="text-[#00F0FF]">[{String(s.order).padStart(2,"0")}]</span>
                    <span className="text-white flex-1">{s.action}</span>
                    {s.connector_type ? (
                      <span className="text-[9px] font-mono uppercase tracking-widest text-[#B57BFF] border border-[#B57BFF]/40 px-1 py-0.5" title={`Real via ${s.connector_type}`}>REAL</span>
                    ) : (
                      <span className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">log</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-auto flex gap-2">
              <input
                placeholder={`target (${pb.target})`}
                value={target[pb.id] || ""}
                onChange={(e) => setTarget({ ...target, [pb.id]: e.target.value })}
                data-testid={`playbook-target-${pb.id}`}
                className="flex-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]"
              />
              <button
                onClick={() => run(pb)}
                data-testid={`playbook-run-${pb.id}`}
                className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                <PlayCircle className="w-3.5 h-3.5" /> Run
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] rounded-sm scan-lines relative" data-testid="playbook-runs">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#121212] text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
          <Terminal className="w-3.5 h-3.5" /> Execution log ({runs.length})
        </div>
        <div className="max-h-80 overflow-y-auto p-4 font-mono text-xs space-y-3">
          {runs.length === 0 && <div className="text-[#52525B]">No runs yet.</div>}
          {runs.map((r) => (
            <div key={r.id} className="border-l-2 border-[#00F0FF]/40 pl-3">
              <div className="text-[#00F0FF]">▶ {r.playbook_name}  <span className="text-[#8E9196]">/ {r.target}</span></div>
              {r.output.map((line, i) => (
                <div key={i} className="text-white/80">{line}</div>
              ))}
              <div className="text-[#52525B]">executed by {r.executed_by}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
